/**
 * Branding settings Card — per-org white-label branding (display name, two
 * surface-specific logos, favicon, mascot). Drives the sidebar logo, app loading
 * screen, and the catcher redo-dialog mascot via
 * resolveBranding() (apps/frontend/src/lib/branding.ts). Unset falls back to the
 * Blue Monk defaults — see DEFAULT_BRANDING.
 *
 * Two logos, one per surface/background:
 *  - Sidebar logo (branding.logoUrl) sits on a DARK surface → light-coloured logo.
 *  - Loading-screen logo (branding.logoLightUrl) sits on a WHITE surface → dark logo.
 * The loading-screen logo falls back to the sidebar logo when empty
 * (resolveBranding), so leaving it blank keeps the single-logo behaviour.
 *
 * The browser tab always shows the PrintRun favicon. A faviconUrl saved earlier
 * is kept as-is on save (round-tripped) but no longer used or editable.
 * Accent color is still schema-ready but not rendered anywhere yet (phase 2).
 */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save, Upload, X, ImageIcon } from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useOrganizationMutations } from '../hooks/useOrganizationMutations';
import { supabase } from '@/integrations/supabase/client';
import type { UpdateOrganizationInput } from '@threadheads/shared';

const VALID_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/svg+xml'];
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

export function BrandingSettingsCard() {
  const { organization } = useOrganization();
  const { updateOrganization } = useOrganizationMutations();

  const [displayName, setDisplayName] = useState(organization?.settings?.branding?.displayName ?? '');
  const [logoUrl, setLogoUrl] = useState(organization?.settings?.branding?.logoUrl ?? '');
  const [logoLightUrl, setLogoLightUrl] = useState(organization?.settings?.branding?.logoLightUrl ?? '');
  const [faviconUrl, setFaviconUrl] = useState(organization?.settings?.branding?.faviconUrl ?? '');
  const [mascotUrl, setMascotUrl] = useState(organization?.settings?.branding?.mascotUrl ?? '');
  const [dirty, setDirty] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoLightUploading, setLogoLightUploading] = useState(false);
  const [mascotUploading, setMascotUploading] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const logoLightInputRef = useRef<HTMLInputElement>(null);
  const mascotInputRef = useRef<HTMLInputElement>(null);

  // Sync fields when organization data changes (mirrors Company/Brands card sync).
  useEffect(() => {
    const b = organization?.settings?.branding;
    setDisplayName(b?.displayName ?? '');
    setLogoUrl(b?.logoUrl ?? '');
    setLogoLightUrl(b?.logoLightUrl ?? '');
    setFaviconUrl(b?.faviconUrl ?? '');
    setMascotUrl(b?.mascotUrl ?? '');
    setDirty(false);
  }, [organization]);

  const uploadImage = async (file: File, prefix: 'logo' | 'logo_light' | 'favicon' | 'mascot'): Promise<string | null> => {
    if (!organization?.id) return null;
    if (!VALID_IMAGE_TYPES.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or SVG image');
      return null;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error('Image must be under 2 MB');
      return null;
    }
    const ext = file.name.split('.').pop();
    const path = `branding/${organization.id}/${prefix}_${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from('product_images')
      .upload(path, file, { cacheControl: '3600', upsert: true });
    if (uploadError) throw uploadError;
    const { data: { publicUrl } } = supabase.storage.from('product_images').getPublicUrl(path);
    return publicUrl;
  };

  const handleLogoUpload = async (file: File) => {
    setLogoUploading(true);
    try {
      const url = await uploadImage(file, 'logo');
      if (url) {
        setLogoUrl(url);
        setDirty(true);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoLightUpload = async (file: File) => {
    setLogoLightUploading(true);
    try {
      const url = await uploadImage(file, 'logo_light');
      if (url) {
        setLogoLightUrl(url);
        setDirty(true);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload logo');
    } finally {
      setLogoLightUploading(false);
    }
  };


  const handleMascotUpload = async (file: File) => {
    setMascotUploading(true);
    try {
      const url = await uploadImage(file, 'mascot');
      if (url) {
        setMascotUrl(url);
        setDirty(true);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload mascot');
    } finally {
      setMascotUploading(false);
    }
  };

  const handleRemoveLogo = () => { setLogoUrl(''); setDirty(true); };
  const handleRemoveLogoLight = () => { setLogoLightUrl(''); setDirty(true); };
  const handleRemoveMascot = () => { setMascotUrl(''); setDirty(true); };

  const handleSave = () => {
    const currentSettings = organization?.settings ?? {};
    const updates: UpdateOrganizationInput = {
      settings: {
        ...currentSettings,
        branding: {
          displayName: displayName.trim() || undefined,
          logoUrl: logoUrl.trim() || undefined,
          logoLightUrl: logoLightUrl.trim() || undefined,
          faviconUrl: faviconUrl.trim() || undefined,
          mascotUrl: mascotUrl.trim() || undefined,
        },
      },
    };
    updateOrganization.mutate(updates, {
      onSuccess: () => setDirty(false),
    });
  };

  const handleCancel = () => {
    const b = organization?.settings?.branding;
    setDisplayName(b?.displayName ?? '');
    setLogoUrl(b?.logoUrl ?? '');
    setLogoLightUrl(b?.logoLightUrl ?? '');
    setFaviconUrl(b?.faviconUrl ?? '');
    setMascotUrl(b?.mascotUrl ?? '');
    setDirty(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Branding</CardTitle>
        <CardDescription>
          White-label the app for your organization: logo, name, and mascot.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="branding_display_name">Display name</Label>
          <Input
            id="branding_display_name"
            type="text"
            value={displayName}
            onChange={(e) => { setDisplayName(e.target.value); setDirty(true); }}
            placeholder={organization?.name || 'ThreadHeads'}
            maxLength={100}
          />
          <p className="text-xs text-muted-foreground">
            Shown in the sidebar, browser tab, and app loading screen. Falls back to your organization name if empty.
          </p>
        </div>

        <div className="space-y-2">
          <Label>Sidebar logo</Label>
          <p className="text-xs text-muted-foreground">
            Shown in the sidebar, on a dark background. Use a light-coloured logo. JPG, PNG, or SVG, max 2 MB.
          </p>
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded border bg-zinc-900 flex items-center justify-center overflow-hidden shrink-0">
              {logoUrl ? (
                <img src={logoUrl} alt="Sidebar logo preview" className="h-10 w-10 object-contain" />
              ) : (
                <ImageIcon className="h-5 w-5 text-zinc-500" />
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" disabled={logoUploading} onClick={() => logoInputRef.current?.click()}>
                {logoUploading ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Upload className="mr-2 h-3 w-3" />}
                {logoUrl ? 'Replace' : 'Upload'}
              </Button>
              {logoUrl && (
                <Button type="button" variant="ghost" size="sm" onClick={handleRemoveLogo}>
                  <X className="mr-1 h-3 w-3" /> Remove
                </Button>
              )}
            </div>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/svg+xml"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleLogoUpload(file);
                e.target.value = '';
              }}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Loading screen logo</Label>
          <p className="text-xs text-muted-foreground">
            Shown on the app loading screen, on a white background. Use a dark-coloured logo. Falls back to your sidebar logo if empty.
          </p>
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded border bg-white flex items-center justify-center overflow-hidden shrink-0">
              {logoLightUrl || logoUrl ? (
                <img src={logoLightUrl || logoUrl} alt="Loading screen logo preview" className="h-10 w-10 object-contain" />
              ) : (
                <ImageIcon className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" disabled={logoLightUploading} onClick={() => logoLightInputRef.current?.click()}>
                {logoLightUploading ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Upload className="mr-2 h-3 w-3" />}
                {logoLightUrl ? 'Replace' : 'Upload'}
              </Button>
              {logoLightUrl && (
                <Button type="button" variant="ghost" size="sm" onClick={handleRemoveLogoLight}>
                  <X className="mr-1 h-3 w-3" /> Remove
                </Button>
              )}
            </div>
            <input
              ref={logoLightInputRef}
              type="file"
              accept="image/jpeg,image/png,image/svg+xml"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleLogoLightUpload(file);
                e.target.value = '';
              }}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Mascot</Label>
          <p className="text-xs text-muted-foreground">
            Shown on production dialogs. Leave empty for a neutral icon.
          </p>
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded border bg-muted flex items-center justify-center overflow-hidden shrink-0">
              {mascotUrl ? (
                <img src={mascotUrl} alt="Mascot preview" className="h-10 w-10 object-contain" />
              ) : (
                <ImageIcon className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" disabled={mascotUploading} onClick={() => mascotInputRef.current?.click()}>
                {mascotUploading ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Upload className="mr-2 h-3 w-3" />}
                {mascotUrl ? 'Replace' : 'Upload'}
              </Button>
              {mascotUrl && (
                <Button type="button" variant="ghost" size="sm" onClick={handleRemoveMascot}>
                  <X className="mr-1 h-3 w-3" /> Remove
                </Button>
              )}
            </div>
            <input
              ref={mascotInputRef}
              type="file"
              accept="image/jpeg,image/png,image/svg+xml"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleMascotUpload(file);
                e.target.value = '';
              }}
            />
          </div>
        </div>

        {dirty && (
          <div className="flex gap-2 pt-2">
            <Button type="button" onClick={handleSave} disabled={updateOrganization.isPending}>
              {updateOrganization.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>
              ) : (
                <><Save className="mr-2 h-4 w-4" />Save</>
              )}
            </Button>
            <Button type="button" variant="outline" onClick={handleCancel} disabled={updateOrganization.isPending}>
              Cancel
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
